import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  PropsWithChildren,
} from "react";
import {useIdleTimer} from "react-idle-timer";
import {AUTO_LOGOUT_TIMES} from "../utils/constants/autoLogout";
import {useAutoLogoutInfo} from "./AutoLogoutInfoContext";
import {useProactiveTokenRefresh} from "../hooks/useProactiveTokenRefresh";

/**
 * AutoLogoutContext
 *
 * Purpose: Manages inactivity timeout and warning countdown for authenticated pages
 *
 * Used by:
 * - AdminRoutesContent (wraps all authenticated /admin routes)
 * - AuthenticatedAdmissionRoutes (wraps all authenticated /admission routes)
 *
 * Behavior:
 * 1. Tracks user activity (mouse, keyboard, etc.) in the current tab only
 * 2. After 15 minutes of inactivity → shows warning countdown (1 minute)
 * 3. If no activity during warning → logs out and sets flag in AutoLogoutInfoContext
 * 4. User can click "Stay logged in" to reset timer
 * 5. Each tab has independent timer (no cross-tab sync)
 *
 * Note: This context does NOT wrap login pages - only authenticated routes.
 * Login pages use AutoLogoutInfoContext to show post-logout message.
 *
 * Timeouts: Configured in AUTO_LOGOUT_TIMES constant
 * - INACTIVITY_TIMEOUT: 15 minutes (15 minutes total before logout)
 * - WARNING_BEFORE_IDLE: 1 minute (warning appears at 15 minutes mark)
 */

interface AutoLogoutContextValue {
  getRemainingTime: () => number;
  resetTimeout: () => void;
  isWarning: boolean;
}

const AutoLogoutContext = createContext<AutoLogoutContextValue | null>(null);

interface AutoLogoutProviderProps {
  userType: "admin" | "re";
  onLogout: () => Promise<void>;
  inactivityTimeout?: number;
  warningBeforeIdle?: number;
}

/**
 * Internal component that enables proactive token refresh.
 * Must be a child of AutoLogoutProvider to access useAutoLogout hook.
 * Exposes check function through ref so it can be called on user activity.
 */
function ProactiveTokenRefresh({
  checkTokenRefreshRef,
}: {
  checkTokenRefreshRef: React.MutableRefObject<(() => Promise<void>) | null>;
}): null {
  const checkAndRefreshToken = useProactiveTokenRefresh();

  // Store check function in ref so it can be called from parent
  useEffect(() => {
    checkTokenRefreshRef.current = checkAndRefreshToken;
    return () => {
      checkTokenRefreshRef.current = null;
    };
  }, [checkAndRefreshToken, checkTokenRefreshRef]);

  return null;
}

export function AutoLogoutProvider({
  children,
  onLogout,
  inactivityTimeout = AUTO_LOGOUT_TIMES.INACTIVITY_TIMEOUT,
  warningBeforeIdle = AUTO_LOGOUT_TIMES.WARNING_BEFORE_IDLE,
}: PropsWithChildren<AutoLogoutProviderProps>) {
  const [isWarning, setIsWarning] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const {clearAutoLoggedOut, setAutoLoggedOut} = useAutoLogoutInfo();
  const checkTokenRefreshRef = useRef<(() => Promise<void>) | null>(null);

  // Clear flag on mount (when user logs back in)
  useEffect(() => {
    clearAutoLoggedOut();
  }, [clearAutoLoggedOut]);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    setAutoLoggedOut();
    await onLogout();
  }, [onLogout, setAutoLoggedOut]);

  const onIdle = useCallback(async () => {
    await handleLogout();
  }, [handleLogout]);

  const onPrompt = useCallback(() => {
    setIsWarning(true);
  }, []);

  const onActive = useCallback(() => {
    setIsWarning(false);
    // Check token refresh immediately when user becomes active
    if (checkTokenRefreshRef.current) {
      checkTokenRefreshRef.current();
    }
  }, []);

  // Use react-idle-timer - tracks timing internally
  const {reset, getRemainingTime} = useIdleTimer({
    timeout: inactivityTimeout,
    promptBeforeIdle: warningBeforeIdle,
    onIdle,
    onPrompt,
    onActive,
    throttle: 500,
    crossTab: false, // Each tab tracks its own activity
  });

  const resetTimeout = useCallback(() => {
    reset();
    onActive();
  }, [reset, onActive]);

  const contextValue = useMemo(
    () => ({getRemainingTime, resetTimeout, isWarning}),
    [getRemainingTime, resetTimeout, isWarning],
  );

  return (
    <AutoLogoutContext.Provider value={contextValue}>
      <ProactiveTokenRefresh checkTokenRefreshRef={checkTokenRefreshRef} />
      {isLoggingOut ? null : children}
    </AutoLogoutContext.Provider>
  );
}

export const useAutoLogout = (): AutoLogoutContextValue => {
  const context = useContext(AutoLogoutContext);
  if (!context) {
    throw new Error("useAutoLogout must be used within AutoLogoutProvider");
  }
  return context;
};
